import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ClipboardList, Plus, AlertTriangle, RefreshCcw, Target } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import {
  useLodgements,
  type LodgementInput,
  type LodgementRecord,
} from '../../hooks/useLodgements';
import {
  useLodgementTargets,
  isAmountTarget,
  TARGET_TYPE_LABELS,
  type LodgementTargetType,
} from '../../hooks/useLodgementTargets';
import { useTenantLenders } from '../../hooks/useTenantLenders';
import {
  OwnerSelect,
  useOwnerOptions,
  useOwnerLabelMap,
  type OwnerOptionGroup,
  type TeamMemberLike,
} from './OwnerSelect';
import { LodgementFormModal } from './LodgementFormModal';
import { LodgementOwnerSummary } from './LodgementOwnerSummary';
import { LodgementRegister } from './LodgementRegister';
import { LodgementCharts } from './LodgementCharts';
import { LodgementTeamCharts } from './LodgementTeamCharts';
import { LodgementTargetsModal } from './LodgementTargetsModal';
import {
  money, moneyShort, attainmentChip, attainmentBar, EMPTY,
} from './LodgementDisplay';

type OutletCtx = {
  userData?: { companyId?: string; role?: string; uid?: string };
  teamMembers?: TeamMemberLike[];
};

/**
 * Team Activity - Lodgements.
 *
 * The manually-entered counterpart to Deal Pipeline. Deal Pipeline reads a
 * live MyCRM feed; tenants without CRM access capture the same
 * lodgement-to-settlement facts here, and the rest of Team Activity reads from
 * whichever source a tenant has.
 *
 * SOURCE SPLIT, enforced by the schema rather than by convention:
 *   ENTERED  the fields in this form
 *   DERIVED  time-to-formal, computed server-side and shown read-only. Period
 *            grouping (month, quarter, FY, ISO week, trailing N days) is NOT
 *            stored or entered: it is derived from dateLodged at read time, so
 *            every period view stays mutually consistent and a new one costs
 *            nothing.
 *   AUTO     settlement date and settled amount, filled by the commission
 *            reconciler. Not form fields at all, so a user cannot forge or
 *            overwrite a settlement.
 *
 * Status values mirror MyCRM `SystemStatus` so the column maps 1:1 when a
 * tenant's LMG API access lands. Do not introduce local status names.
 *
 * THE PAGE IS A PER-PERSON BOOK, NOT A FLAT TABLE. The workbook this replaced
 * was one tab per person with targets on top and a monthly rollup underneath,
 * and the questions it was kept to answer are all of the form "how is X
 * tracking". A register with no owner column answers none of them, so the page
 * carries three things beyond the raw rows: WHO owns each row, a per-person
 * summary over the whole period, and a way to correct a row that was typed
 * wrong. Settlement figures are pulled by the reconciler rather than waited on.
 */

const CAN_ENTER = new Set(['super_admin', 'administrator', 'broker', 'credit_analyst', 'broker_assistant']);

/**
 * Reconciliation rewrites financial figures across the whole tenant, so the
 * callable gates it to these two roles. Mirrored here so the UI does not offer
 * a button that is going to be refused; the server remains the enforcement.
 */
const CAN_SYNC = new Set(['super_admin', 'administrator']);

/**
 * A target is what someone is measured against, so the person being measured
 * must not be able to move it. Same two roles the target callables enforce
 * (`upsertLodgementTarget` / `deleteLodgementTarget`); mirrored here so the UI
 * does not offer a button that is going to be refused.
 */
const CAN_MANAGE_TARGETS = new Set(['super_admin', 'administrator']);

/**
 * `ownerId` is deliberately ABSENT, not the caller's uid.
 *
 * An omitted owner is what tells the server "this is the caller's own work",
 * and the server resolves it to the caller's primary owner ref. Seeding the
 * form with a bare uid would both send an unattributable value (a uid is not a
 * namespaced ref) and stop a person with no login from owning their own row.
 */
function emptyForm(): LodgementInput {
  return {
    clientName: '', assistantId: null,
    dateLodged: new Date().toISOString().slice(0, 10),
    dateApproved: null, lender: '', lodgeAmountCents: 0,
    status: 'Lodged', due: null, mirs: [],
    oneTouch: false, googleReviewAsked: false, googleReviewReceived: false,
    structure: null, remarks: null, customFields: {},
  };
}

/**
 * A stored row, narrowed back to what the form may send.
 *
 * Field by field rather than a spread, because a `LodgementRecord` carries the
 * server-owned block (id, settledDate, settledAmountCents, daysToFormal,
 * matchedLoanId, audit stamps) and `LodgementInputSchema` is `.strict()`: a
 * spread would put those keys on the wire and the update would be rejected
 * outright. Listing the fields also means a new input field has to be added
 * here consciously rather than leaking in.
 */
function formFromRecord(r: LodgementRecord): LodgementInput {
  return {
    clientName: r.clientName,
    ownerId: r.ownerId,
    assistantId: r.assistantId ?? null,
    dateLodged: r.dateLodged,
    dateApproved: r.dateApproved ?? null,
    lender: r.lender,
    lodgeAmountCents: r.lodgeAmountCents,
    status: r.status,
    due: r.due ?? null,
    mirs: (r.mirs ?? []).map((m) => ({
      category: m.category,
      reason: m.reason ?? '',
      raisedOn: m.raisedOn ?? null,
      resolvedOn: m.resolvedOn ?? null,
    })),
    oneTouch: Boolean(r.oneTouch),
    googleReviewAsked: Boolean(r.googleReviewAsked),
    googleReviewReceived: Boolean(r.googleReviewReceived),
    structure: r.structure ?? null,
    remarks: r.remarks ?? null,
    customFields: r.customFields ?? {},
  };
}

/**
 * Period options. The window is a DATE RANGE, never a stored bucket, so the
 * same control drives the register, the stats and the targets, and they cannot
 * disagree. Adding "last 30 days" here costs one entry and no migration.
 */
function fyStartYear(d = new Date()): number {
  return d.getUTCMonth() + 1 >= 7 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
}

/**
 * What SHAPE of window an option is, carried alongside the dates.
 *
 * The gauge strip and the weekly split are only meaningful over a target's own
 * period (a month or a quarter), and inferring that from the date span would be
 * guesswork: a 30-day trailing window and a February are both 28 to 31 days
 * long and mean entirely different things. The option knows what it is, so it
 * says so.
 */
export type PeriodKind = 'fy' | 'quarter' | 'month' | 'range';

export interface PeriodOption {
  key: string;
  label: string;
  from: string;
  to: string;
  kind: PeriodKind;
  /** Optgroup heading in the period select. */
  group: string;
}

function periodOptions(): PeriodOption[] {
  const y = fyStartYear();
  const iso = (yy: number, mm: number, dd: number) =>
    `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  const lastDay = (yy: number, mm: number) => new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  const opts: PeriodOption[] = [];

  const today = new Date();
  const back = (n: number) => new Date(today.getTime() - n * 86_400_000).toISOString().slice(0, 10);
  opts.push({ key: 'last30', label: 'Last 30 days', from: back(30), to: today.toISOString().slice(0, 10), kind: 'range', group: 'Rolling' });
  opts.push({ key: 'last14', label: 'Last 14 days', from: back(14), to: today.toISOString().slice(0, 10), kind: 'range', group: 'Rolling' });

  // Every offered financial year carries its own quarters and months. When
  // only the current FY's existed, history was unreachable: with FY 26-27
  // current there was no "Jun 26" at all, and "3rd Qtr (Jan-Mar)" silently
  // meant 2027. Backfilled tenants live in exactly those prior windows.
  for (let backYears = 0; backYears <= 2; backYears++) {
    const fy = y - backYears;
    const fyLabel = `FY ${String(fy).slice(2)}-${String(fy + 1).slice(2)}`;
    opts.push({
      key: backYears === 0 ? 'fy' : `fy-${fy}`,
      label: `${fyLabel} (full year)`,
      from: iso(fy, 7, 1),
      to: iso(fy + 1, 6, 30),
      kind: 'fy',
      group: fyLabel,
    });
    const quarters: Array<[string, number, number]> = [
      ['1st Qtr (Jul-Sep', 7, fy], ['2nd Qtr (Oct-Dec', 10, fy],
      ['3rd Qtr (Jan-Mar', 1, fy + 1], ['4th Qtr (Apr-Jun', 4, fy + 1],
    ];
    for (const [stem, startMonth, yy] of quarters) {
      const endMonth = startMonth + 2;
      opts.push({
        key: `${fy}-${stem}`,
        label: `${stem} ${yy})`,
        from: iso(yy, startMonth, 1),
        to: iso(yy, endMonth, lastDay(yy, endMonth)),
        kind: 'quarter',
        group: fyLabel,
      });
    }
    for (let i = 0; i < 12; i++) {
      const abs = 6 + i;
      const yy = fy + Math.floor(abs / 12);
      const mm = (abs % 12) + 1;
      const name = new Date(Date.UTC(yy, mm - 1, 1)).toLocaleString('en-AU', { month: 'short', timeZone: 'UTC' });
      opts.push({ key: `${yy}-${mm}`, label: `${name} ${String(yy).slice(2)}`, from: iso(yy, mm, 1), to: iso(yy, mm, lastDay(yy, mm)), kind: 'month', group: fyLabel });
    }
  }
  return opts;
}

export default function LodgementsPage(): React.ReactElement {
  const { userData, teamMembers } = useOutletContext<OutletCtx>() ?? {};
  const role = userData?.role ?? '';
  const companyId = userData?.companyId;

  const periods = useMemo(() => periodOptions(), []);
  const [periodKey, setPeriodKey] = useState<string>('fy');
  const period = periods.find((p) => p.key === periodKey) ?? periods[0];

  // Owner filter for the register. Empty means "no ownerId key on the request",
  // which is company-wide for a company-wide role and a no-op for anyone else,
  // since the server narrows the query by role regardless of what is sent.
  const [ownerFilter, setOwnerFilter] = useState<string>('');

  const canEnter = useMemo(() => CAN_ENTER.has(role), [role]);
  const canSync = useMemo(() => CAN_SYNC.has(role), [role]);
  const canManageTargets = useMemo(() => CAN_MANAGE_TARGETS.has(role), [role]);

  const {
    rows, stats, scope, loading, loadingRest, error, refresh,
    create, update, reconcileSettlements, rowsComplete,
  } = useLodgements(companyId, {
    from: period.from,
    to: period.to,
    ownerId: ownerFilter || undefined,
  });
  /**
   * No ownerId: the register's owner filter must not narrow the target list.
   * The manager needs every target the company has, and the gauge strip picks
   * out the person it is drawing.
   */
  const targets = useLodgementTargets(companyId, period.from, period.to);
  const { lenders, source: lenderSource } = useTenantLenders(companyId);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<LodgementRecord | null>(null);
  const [form, setForm] = useState<LodgementInput>(() => emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncErr, setSyncErr] = useState<string | null>(null);

  /**
   * Targets manager. `null` means closed; an object means open, optionally
   * prefilled by whichever "no target set" gauge sent the user here.
   */
  const [targetsModal, setTargetsModal] =
    useState<{ ownerId?: string; type?: LodgementTargetType } | null>(null);

  /**
   * Who gets an owner picker, mirroring the gates this page already has rather
   * than inventing a role list:
   *
   *   FILTER  `scope === 'company'`. That is the server's own answer to "does
   *           this caller read company-wide", already rendered on by the
   *           scope notice below. A role scoped to its own rows gets no picker,
   *           because the server ignores a client ownerId for them anyway.
   *
   *   FORM    company-wide read AND on this page's existing CAN_ENTER list.
   *           The intersection is exactly the roles the callable lets write an
   *           arbitrary owner: office_admin reads company-wide but cannot
   *           enter, and a broker can enter but only ever as themselves.
   */
  const isCompanyWide = scope === 'company';
  const canPickOwner = isCompanyWide && canEnter;
  const ownerGroups = useOwnerOptions(companyId, isCompanyWide, teamMembers);
  const ownerLabels = useOwnerLabelMap(ownerGroups);

  /**
   * The picker must be able to show the owner a row already has, even when that
   * ref is not in any of the three lists (a team member who has since left, a
   * deactivated analyst record). Without this the select would render blank and
   * read as "no owner", and the first save would look like a reattribution.
   */
  const editOwnerGroups = useMemo<OwnerOptionGroup[]>(() => {
    const current = editing?.ownerId;
    if (!current || ownerLabels.has(current)) return ownerGroups;
    return [
      { key: 'current', label: 'Current owner', options: [{ ref: current, label: current }] },
      ...ownerGroups,
    ];
  }, [editing, ownerGroups, ownerLabels]);

  /**
   * The one person the gauge strip is about, or null when the question is not
   * about one person.
   *
   * Two ways to be that person. An admin NAMES one with the owner filter. An
   * own-scope reader IS one by definition: the server narrowed every row they
   * can see to themselves, so a single distinct ownerId across the window is
   * that fact rather than an inference. The all-rows-agree check is what keeps
   * it honest if the scoping ever widens.
   */
  const soleOwnerRef = useMemo<string | null>(() => {
    if (ownerFilter) return ownerFilter;
    if (scope !== 'own' || rows.length === 0) return null;
    const first = rows[0].ownerId;
    return rows.every((r) => r.ownerId === first) ? first : null;
  }, [ownerFilter, scope, rows]);

  /**
   * The per-person chart strip needs a period a target can be judged over, so a
   * month or a quarter. Weekly charts inside it render for a month only.
   */
  const showPersonCharts =
    soleOwnerRef !== null && (period.kind === 'month' || period.kind === 'quarter');

  /**
   * The team strip is the Everyone view, so it needs both a company-wide reader
   * AND no owner filter. Filtering to one person and still drawing "by person"
   * charts would render a single bar and call it a team.
   */
  const showTeamCharts = isCompanyWide && !ownerFilter;

  const set = useCallback(
    <K extends keyof LodgementInput>(k: K, v: LodgementInput[K]) =>
      setForm((f) => ({ ...f, [k]: v })),
    []
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(emptyForm());
    setSaveErr(null);
    setFormMode('create');
  }, []);

  const openEdit = useCallback((r: LodgementRecord) => {
    setEditing(r);
    setForm(formFromRecord(r));
    setSaveErr(null);
    setFormMode('edit');
  }, []);

  const closeForm = useCallback(() => {
    setFormMode(null);
    setEditing(null);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveErr(null);
    try {
      if (formMode === 'edit' && editing) {
        /**
         * AN EDIT IS NOT A REATTRIBUTION.
         *
         * ownerId goes on the wire ONLY when the admin actively moved the
         * picker to a different owner. Anything else, including re-selecting
         * the value the row already had, sends no ownerId at all, and the
         * server then keeps the stored owner verbatim without re-resolving it.
         * That behaviour is deliberate and tested server-side: re-probing a
         * preserved owner would make a row uneditable the moment its broker
         * record was merged away, which is exactly when it needs correcting.
         */
        const chosen = form.ownerId?.trim();
        const reattributing = Boolean(chosen) && chosen !== editing.ownerId;
        await update(editing.id, reattributing ? form : { ...form, ownerId: undefined });
      } else {
        await create(form);
      }
      closeForm();
      setForm(emptyForm());
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [create, update, form, formMode, editing, closeForm]);

  const runSync = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    setSyncErr(null);
    try {
      const report = await reconcileSettlements();
      const s = report.summary;
      const unmatched = Math.max(0, s.total - s.matched);
      setSyncMsg(
        `Matched ${s.matched} of ${s.total} unsettled lodgement${s.total === 1 ? '' : 's'} `
        + `(${s.matchedByName} by name, ${s.matchedByCorroboration} corroborated), `
        + `${unmatched} left unmatched. ${report.written} row${report.written === 1 ? '' : 's'} updated. `
        + `Skipped ${report.skippedAlreadySettled} already settled and `
        + `${report.skippedManualOverride} manual override${report.skippedManualOverride === 1 ? '' : 's'}.`
      );
    } catch (e) {
      console.error('[LodgementsPage] settlement sync failed', e);
      setSyncErr(e instanceof Error ? e.message : 'Could not sync settlements');
    } finally {
      setSyncing(false);
    }
  }, [reconcileSettlements]);

  // The section's shared RequireRole admits `marketing` (correct for Lead
  // Capture), but lodgements carry client names and loan values. Deny here
  // rather than restructuring the shared guard. min-h-[50vh], not min-h-screen.
  if (role === 'marketing') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="max-w-md rounded-lg border border-gray-700 bg-gray-900/40 p-6 text-center">
          <h2 className="text-lg font-semibold text-white">Not available</h2>
          <p className="mt-2 text-sm text-gray-400">
            Lodgements is not available to the marketing role. Use Leads for
            enquiry and campaign activity.
          </p>
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'Lodgements', value: String(stats.lodgementCount), hint: `${moneyShort(stats.lodgedAmountCents)} submitted`, tone: 'text-blue-300' },
    { label: 'Settled', value: String(stats.settledCount), hint: `${moneyShort(stats.settledAmountCents)} settled`, tone: 'text-emerald-300' },
    { label: 'Conversion', value: stats.conversionPct === null ? EMPTY : `${stats.conversionPct}%`, hint: 'lodged to settled', tone: 'text-amber-300' },
    { label: 'Median to formal', value: stats.medianDaysToFormal === null ? EMPTY : `${stats.medianDaysToFormal}d`, hint: 'computed, not typed', tone: 'text-gray-400' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ClipboardList}
        title="Lodgements"
        subtitle="Manually captured lodgement activity. Settlement figures auto-fill from the commission pipeline."
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canManageTargets && (
              <button
                type="button"
                onClick={() => setTargetsModal({})}
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-3 py-1.5 text-sm font-semibold text-gray-200 hover:bg-gray-800"
                title="Create, replace or retire the targets people are measured against"
              >
                <Target className="h-4 w-4" />
                Manage targets
              </button>
            )}
            {canSync && (
              <button
                type="button"
                onClick={() => { void runSync(); }}
                disabled={syncing}
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-3 py-1.5 text-sm font-semibold text-gray-200 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                title="Match unsettled lodgements against settled commission records"
              >
                <RefreshCcw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Sync settlements'}
              </button>
            )}
            {canEnter && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
              >
                <Plus className="h-4 w-4" />
                New lodgement
              </button>
            )}
          </div>
        }
      />

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-700/40 bg-red-900/10 p-4 text-[13px] text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Could not load lodgements.</p>
            <p className="mt-1 text-red-200/80">{error}</p>
          </div>
        </div>
      )}

      {syncMsg && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-900/10 p-3 text-[12px] text-emerald-200">
          {syncMsg}
        </div>
      )}
      {syncErr && (
        <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-3 text-[12px] text-red-200">
          Settlement sync failed: {syncErr}
        </div>
      )}

      {scope === 'own' && !error && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 text-[12px] text-gray-400">
          Your role sees your own lodgements only.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{c.label}</div>
            <div className="mt-1 text-3xl font-bold text-white">{loading ? '…' : c.value}</div>
            <div className={`mt-1 text-[11px] ${c.tone}`}>{c.hint}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-700 bg-gray-900/40 p-3">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Period
          </label>
          <select
            value={periodKey}
            onChange={(e) => setPeriodKey(e.target.value)}
            className="rounded-md border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-sm text-white"
          >
            {[...new Set(periods.map((p) => p.group))].map((group) => (
              <optgroup key={group} label={group}>
                {periods.filter((p) => p.group === group).map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="text-[11px] text-gray-500">
          {period.from} to {period.to}
        </div>
        {isCompanyWide && (
          <div>
            <label
              htmlFor="lodgement-owner-filter"
              className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500"
            >
              Owner
            </label>
            <OwnerSelect
              id="lodgement-owner-filter"
              value={ownerFilter}
              onChange={setOwnerFilter}
              groups={ownerGroups}
              emptyLabel="Everyone"
              className="rounded-md border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-sm text-white"
            />
          </div>
        )}
        <div className="ml-auto text-right">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">In this period</div>
          <div className="text-sm font-semibold text-white">
            {rows.length.toLocaleString()} lodgement{rows.length === 1 ? '' : 's'}
          </div>
          {/*
            Say so when the window is short. Every figure on this page derives
            from these rows, so an unstated cap would read as a complete total
            that is quietly wrong.
          */}
          {!loading && !loadingRest && !rowsComplete && (
            <div className="text-[11px] text-amber-300">partial period; totals understated</div>
          )}
        </div>
      </div>

      {/*
        The company-wide target list, suppressed when the gauge strip is up.
        The gauges answer the same question for the person actually selected,
        and stacking everyone else's bars above them would put two different
        target readouts on one screen.
      */}
      {!showPersonCharts && !showTeamCharts && (targets.progress.length > 0 || targets.error) && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40">
          <div className="border-b border-gray-700 px-4 py-3">
            <h2 className="text-base font-semibold text-white">Targets</h2>
            <p className="mt-0.5 text-[11px] text-gray-400">
              Progress for the selected period. Settlement targets measure the
              settlement date, lodgement targets the lodgement date.
            </p>
          </div>
          <div className="space-y-2 p-4">
            {targets.error && (
              <p className="text-[12px] text-red-300">Could not load targets: {targets.error}</p>
            )}
            {/*
              ONE attainment scale for the whole feature. These bars used to run
              on their own 100/85 breakpoints while the summary panel's chips ran
              on 100/70, so the same percentage could be amber here and red four
              sections down. Both now come from `LodgementDisplay`.
            */}
            {targets.progress.filter((p) => !p.notApplicable).map((p) => {
              const fmt = isAmountTarget(p.type) ? money : (n: number) => String(n);
              return (
                <div key={p.targetId} className="grid grid-cols-[12rem_1fr_4rem] items-center gap-3">
                  <div className="truncate text-sm text-gray-300" title={ownerLabels.get(p.ownerId) ?? p.ownerId}>
                    {TARGET_TYPE_LABELS[p.type]}
                    <span className="ml-1 text-[11px] text-gray-500">
                      {ownerLabels.get(p.ownerId) ?? p.ownerId}
                    </span>
                  </div>
                  <div className="h-5 overflow-hidden rounded bg-gray-700/40">
                    <div
                      className={`h-full ${attainmentBar(p.percent)}`}
                      style={{ width: `${Math.min(100, Math.max(0, p.percent))}%` }}
                      title={`${fmt(p.actual)} of ${fmt(p.target)}`}
                    />
                  </div>
                  <div className="text-right">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${attainmentChip(p.percent)}`}>
                      {p.percent}%
                    </span>
                  </div>
                </div>
              );
            })}
            {!targets.loading && !targets.error && targets.progress.filter((p) => !p.notApplicable).length === 0 && (
              <p className="text-[12px] text-gray-400">
                No targets apply to this period.
              </p>
            )}
          </div>
        </div>
      )}

      {/*
        The workbook's per-month furniture, restored as charts: deals by week,
        value by week, and the settlement comparison, plus the two chips.
      */}
      {showPersonCharts && soleOwnerRef && !error && (
        <LodgementCharts
          rows={rows}
          ownerId={soleOwnerRef}
          ownerLabel={ownerLabels.get(soleOwnerRef) ?? (scope === 'own' ? 'Your lodgements' : soleOwnerRef)}
          periodLabel={period.label}
          periodKind={period.kind === 'month' ? 'month' : 'quarter'}
          from={period.from}
          to={period.to}
          progress={targets.progress}
          canManageTargets={canManageTargets}
          onAddTarget={(type) => setTargetsModal({ ownerId: soleOwnerRef, type })}
        />
      )}

      {/* The workbook's quarterly Main sheets: the team, drawn as bars. */}
      {showTeamCharts && !error && (
        <LodgementTeamCharts
          rows={rows}
          ownerLabels={ownerLabels}
          progress={targets.progress}
          periodLabel={period.label}
          loading={loading}
        />
      )}

      {/*
        The per-person panel reads the whole window, NOT a page of it. A summary
        that changed every time the reader turned a page is the one thing a
        summary must not do.
      */}
      {isCompanyWide && !error && (
        <LodgementOwnerSummary
          rows={rows}
          rowsComplete={rowsComplete}
          loading={loading}
          ownerLabels={ownerLabels}
          targets={targets.progress}
          // Four week columns only make sense inside one month. Across a
          // quarter or an FY, "1st week" would mean three or twelve different
          // weeks stacked in one cell.
          weeklySplit={period.kind === 'month'}
        />
      )}

      <LodgementRegister
        rows={rows}
        rowsComplete={rowsComplete}
        loading={loading}
        loadingRest={loadingRest}
        showOwner={isCompanyWide}
        ownerLabels={ownerLabels}
        canEnter={canEnter}
        onEdit={openEdit}
      />


      {formMode && (
        <LodgementFormModal
          mode={formMode}
          form={form}
          onChange={set}
          canPickOwner={canPickOwner}
          ownerGroups={formMode === 'edit' ? editOwnerGroups : ownerGroups}
          lenders={lenders}
          lenderSource={lenderSource}
          settlement={editing}
          saving={saving}
          saveErr={saveErr}
          onCancel={closeForm}
          onSave={() => { void save(); }}
        />
      )}

      {targetsModal && (
        <LodgementTargetsModal
          progress={targets.progress}
          loading={targets.loading}
          error={targets.error}
          periodLabel={period.label}
          ownerGroups={ownerGroups}
          ownerLabels={ownerLabels}
          createTarget={targets.createTarget}
          retireTarget={targets.retireTarget}
          initial={targetsModal.type ? targetsModal : null}
          onClose={() => setTargetsModal(null)}
        />
      )}
    </div>
  );
}
